import { useMemo } from 'react';
import type { FormField, ScoringConfig, ScoreRange } from '../types/forms';
import { normalizeFieldOption } from '../types/forms';
import type { RuleCalculationEntry } from './useConditionalLogic';

// =====================================================
// TYPES
// =====================================================

export interface ScoreResult {
  [variableId: string]: {
    totalPoints: number;
    range: ScoreRange | null;
  };
}

interface UseFormScoringProps {
  fields: FormField[];
  formData: Record<string, any>;
  scoringConfig?: ScoringConfig;
  ruleCalculations?: RuleCalculationEntry[];
}

interface UseFormScoringReturn {
  scores: ScoreResult | null;
  isEnabled: boolean;
}

// =====================================================
// SCORE CALCULATION ENGINE
// =====================================================

const SCORABLE_FIELD_TYPES = [
  'radio', 'select', 'checkbox', 'multiselect',
  'picture_choice', 'yes_no', 'opinion_scale', 'nps', 'rating',
] as const;

function findMatchingRange(points: number, ranges: ScoreRange[]): ScoreRange | null {
  if (!ranges?.length) return null;

  for (const range of ranges) {
    if (points >= range.min && points <= range.max) {
      return range;
    }
  }

  return null;
}

export function calculateScores(
  fields: FormField[],
  formData: Record<string, any>,
  scoringConfig: ScoringConfig,
  ruleCalculations?: RuleCalculationEntry[]
): ScoreResult | null {
  if (!scoringConfig?.enabled || !scoringConfig.variables?.length) {
    return null;
  }

  const totals: Record<string, number> = {};
  scoringConfig.variables.forEach(v => { totals[v.id] = 0; });

  // 1. Static option-based scoring
  fields.forEach(field => {
    if (!SCORABLE_FIELD_TYPES.includes(field.type as any)) return;

    const answer = formData[field.id];
    if (answer === undefined || answer === null || answer === '') return;

    const options = (field.options || []).map(normalizeFieldOption);

    const selectedValues: string[] = Array.isArray(answer)
      ? answer
      : [String(answer)];

    selectedValues.forEach(selectedValue => {
      const matchedOption = options.find(opt => opt.value === selectedValue);
      if (!matchedOption?.scores) return;

      matchedOption.scores.forEach(score => {
        if (totals[score.variableId] !== undefined) {
          totals[score.variableId] += score.points;
        }
      });
    });
  });

  // 2. Rule-based calculation adjustments
  if (ruleCalculations?.length) {
    for (const calc of ruleCalculations) {
      if (totals[calc.variableId] === undefined) continue;
      switch (calc.operation) {
        case 'add':
          totals[calc.variableId] += calc.value;
          break;
        case 'subtract':
          totals[calc.variableId] -= calc.value;
          break;
        case 'multiply':
          totals[calc.variableId] *= calc.value;
          break;
        case 'divide':
          if (calc.value !== 0) totals[calc.variableId] /= calc.value;
          break;
      }
    }
  }

  const result: ScoreResult = {};
  scoringConfig.variables.forEach(variable => {
    const points = totals[variable.id];
    const matchedRange = findMatchingRange(points, variable.ranges);
    result[variable.id] = {
      totalPoints: points,
      range: matchedRange,
    };
  });

  return result;
}

// =====================================================
// REACTIVE HOOK
// =====================================================

export function useFormScoring({
  fields,
  formData,
  scoringConfig,
  ruleCalculations,
}: UseFormScoringProps): UseFormScoringReturn {
  const isEnabled = !!scoringConfig?.enabled && (scoringConfig.variables?.length ?? 0) > 0;

  const scores = useMemo(() => {
    if (!isEnabled || !scoringConfig) return null;
    return calculateScores(fields, formData, scoringConfig, ruleCalculations);
  }, [fields, formData, scoringConfig, isEnabled, ruleCalculations]);

  return { scores, isEnabled };
}
